import { useFetcher, useLocation } from '@remix-run/react'
import { LogOutIcon, MailIcon, SquareUserRoundIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { LogoHorizontal } from '~/components/Logo/LogoHorizontal'
import { ShopLink } from '~/components/shop-link'
import { makeShopOwnPath } from '~/helpers/routes'
import { useAnalyticsEnabled } from '~/hooks/data/use-analytics-enabled'
import { useImpersonatedSellerId } from '~/hooks/data/use-impersonated-seller-id'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import {
  Avatar,
  Badge,
  Button,
  Disclosure,
  Dropdown,
  Portal,
  Switch,
} from '~/ui'
import { cn } from '~/utils/classnames'
import { getImageKitUrl } from '~/utils/image'

import { NavbarButton } from './navbar-button'
import { NAVIGATION_ITEMS } from './navbar-constants'
import { NavbarOnboardingChecklist } from './navbar-onboarding-checklist'

const SUPPORT_EMAIL =
  typeof process !== 'undefined'
    ? process.env.SUPPORT_EMAIL
    : window.ENV?.SUPPORT_EMAIL

type NavBarProps = {
  className?: string
}

export const Navbar = ({ className }: NavBarProps) => {
  const { t } = useTranslation()

  const fetcher = useFetcher()
  const location = useLocation()

  const shop = useShop()
  const seller = useOptionalSeller()
  const analyticsEnabled = useAnalyticsEnabled()
  const impersonatedSellerId = useImpersonatedSellerId()

  const isShopPublished = shop.publishedAt !== null

  const handleToggleAnalytics = (isEnabled: boolean) => {
    fetcher.submit(isEnabled ? { analyticsEnabled: true } : {}, {
      method: 'post',
      action: makeShopOwnPath(shop, '/session-apex?index'),
    })
  }

  return (
    <nav
      className={cn(
        'text-foreground hide-scrollbar relative flex h-full flex-col gap-4 overflow-y-scroll p-3 pr-1',
        className
      )}
    >
      {/* header */}
      <header className="flex w-full items-center justify-between gap-3">
        <Avatar size="sm">
          <Avatar.Fallback />
          {shop.avatarSrc && (
            <Avatar.Image
              src={getImageKitUrl(shop.avatarSrc, {
                width: 100,
                height: 100,
                format: 'webp',
              })}
            />
          )}
        </Avatar>
        <div className="flex h-full flex-1 flex-col justify-center gap-0.5 pr-8">
          <h1 className="text-md truncate font-medium">{shop.name}</h1>
          <Badge className="w-fit">
            <Badge.Status status={isShopPublished ? 'success' : 'error'} />
            {t(`noun.${isShopPublished ? 'live' : 'not_live'}`)}
          </Badge>
        </div>
      </header>

      {!isShopPublished && <NavbarOnboardingChecklist />}

      {/* main */}
      <ol className="space-y-1">
        {NAVIGATION_ITEMS.map((item) => {
          const isGroup = 'children' in item

          return (
            <li key={item.name}>
              {!isGroup && (
                <NavbarButton icon={item.icon} asChild>
                  <ShopLink to={item.path}>{t(item.label)}</ShopLink>
                </NavbarButton>
              )}
              {isGroup && (
                <Disclosure
                  defaultOpen={item.children.some(
                    (child) =>
                      location.pathname === makeShopOwnPath(shop, child.path)
                  )}
                >
                  <Disclosure.Trigger asChild className="justify-start">
                    <NavbarButton icon={item.icon}>
                      {t(item.label)}
                    </NavbarButton>
                  </Disclosure.Trigger>
                  <Disclosure.Content>
                    <ol className="border-border/8 ml-4.5 flex flex-col gap-2 overflow-hidden border-l pr-0.5 pb-0.5 pl-3">
                      {item.children.map((child) => {
                        return (
                          <li key={child.name} className="first:mt-2">
                            <NavbarButton asChild>
                              <ShopLink to={child.path}>
                                {t(child.label)}
                              </ShopLink>
                            </NavbarButton>
                          </li>
                        )
                      })}
                    </ol>
                  </Disclosure.Content>
                </Disclosure>
              )}
            </li>
          )
        })}
        <li>
          <NavbarButton asChild icon="analytics">
            <label className="flex cursor-pointer gap-2">
              <span> {t('page.analytics.title')}</span>
              <Switch
                className="ml-auto"
                checked={analyticsEnabled}
                onChange={(e) => handleToggleAnalytics(e.target.checked)}
              />
            </label>
          </NavbarButton>
        </li>
      </ol>

      {/* footer  */}
      <div className="mt-auto space-y-3">
        <div className="bg-over/2 border-border/4 text-foreground rounded-xl border p-2">
          <LogoHorizontal width={'56px'} className="mt-1 mb-3 opacity-30" />

          <p className="text-xs font-medium">
            {t('components.nav_bar.beta_version_card.title')}
          </p>
          <p className="text-foreground/64 mt-0.5 text-xs font-medium">
            {t('components.nav_bar.beta_version_card.description')}
          </p>

          <Button asChild size="sm" variant="outline" className="mt-3">
            <a href={`mailto:${SUPPORT_EMAIL}`}>
              <MailIcon className="opacity-64" />
              {t('components.nav_bar.beta_version_card.button_label')}
            </a>
          </Button>
        </div>
        {impersonatedSellerId && (
          <form
            method="post"
            action={makeShopOwnPath(shop, '/stop-impersonating')}
          >
            <NavbarButton type="submit">
              {t('action.stop_impersonating')}
            </NavbarButton>
          </form>
        )}
        <Dropdown>
          <Dropdown.Trigger asChild>
            <NavbarButton icon="account">
              <span className="block overflow-hidden text-ellipsis">
                {seller!.name || seller!.email}
              </span>
            </NavbarButton>
          </Dropdown.Trigger>
          <Portal>
            <Dropdown.Items data-theme="protocol-dark" className="max-w-60">
              <Dropdown.Item asChild>
                <ShopLink to={'/account'}>
                  <SquareUserRoundIcon />
                  {t('page.account.title')}
                </ShopLink>
              </Dropdown.Item>
              <Dropdown.Divider />
              <form action={makeShopOwnPath(shop, '/logout')} method="post">
                <Dropdown.Item className="text-foreground/64" type="submit">
                  <LogOutIcon />
                  {t('action.log_out')}
                </Dropdown.Item>
              </form>
            </Dropdown.Items>
          </Portal>
        </Dropdown>
      </div>
    </nav>
  )
}
